import React, { useEffect, useState } from 'react'
import {
  Form,
  Button,
  Container,
  Row,
  Col,
  Dropdown,
  DropdownButton,
  Spinner,
} from 'react-bootstrap'
import { Footer, KonektoNavbar } from '../../components'
import './AddCompany.scss'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { companyService } from '../../services/companyService'
import { getMyCompany } from '../../store/Actions'
import { imageService } from '../../services/imageService'
import { toast } from 'react-toastify'
import { hasValue } from '../../helpers'
import { companyTransferService } from '../../services/companyTransferService'
import { NUM_OF_EMPLOYEES_OPTIONS } from '../../constants/config'

export default function AddCompany() {
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const { token } = useSelector(state => state.authentication)
  const { myCompany } = useSelector(state => state.company)

  const cities = useSelector(state => state.utility?.cities ?? [])

  const [formData, setFormData] = useState({
    jib: '',
    name: '',
    description: '',
    phone: '',
    address: '',
    zipCode: '',
    city: '',
    email: '',
    website: '',
    numOfEmployees: '',
    logoPath: null,
  })
  const [type, setType] = useState([])
  const [previewImage, setPreviewImage] = useState(null)

  const [companyAlreadyExists, setCompanyAlreadyExists] = useState(false)
  const [companyAlreadyExistsData, setCompanyAlreadyExistsData] = useState(null)
  const [companyTransferRequestLoader, setCompanyTransferRequestLoader] =
    useState(false)

  const [registrationFormErrors, setRegistrationFormErrors] = useState(null)
  const [registrationResponseErrors, setRegistrationResponseErrors] =
    useState(null)
  const [registrationLoader, setRegistrationLoader] = useState(false)

  const typeOptions = [
    'Distribucija',
    'Servis',
    'Proizvodnja',
    'Proizvodnja po narudzbi',
  ]

  const toggleType = option => {
    setType(prevType =>
      prevType.includes(option)
        ? prevType.filter(item => item !== option)
        : [...prevType, option],
    )
  }

  const handleChange = e => {
    const { name, value } = e.target
    setFormData({
      ...formData,
      [name]: value,
    })
  }

  const handleFileChange = async e => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0]
      const formData = new FormData()
      formData.append('images[]', file)

      const response = await imageService.uploadImage(formData)
      if (response.isOk && response.data) {
        const imagePath = response.data.paths[0]

        setPreviewImage(imagePath)
        setFormData(prevState => ({
          ...prevState,
          logoPath: imagePath,
        }))
      }
    }
  }

  const handleSubmit = async e => {
    setRegistrationFormErrors(null)
    e.preventDefault()

    let newErrors = {}

    if (formData?.jib?.trim() === '') {
      newErrors = { ...newErrors, jib: 'JIB ne može biti prazan' }
    }

    if (formData?.name?.trim() === '') {
      newErrors = { ...newErrors, name: 'Ime firme ne može biti prazno' }
    }

    if (formData?.phone?.trim() === '') {
      newErrors = { ...newErrors, phone: 'Telefon ne može biti prazan' }
    }

    if (Object.keys(newErrors).length > 0) {
      setRegistrationFormErrors(newErrors)
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }

    setRegistrationLoader(true)

    const data = {
      jib: formData.jib,
      name: formData.name,
      description: formData.description,
      phone: formData.phone,
      address: formData.address,
      zip_code: formData.zipCode,
      city: formData.city,
      email: formData.email,
      website: formData.website,
      num_of_employees: formData.numOfEmployees,
      type: type?.join(','),
      cover_image: formData.logoPath,
    }

    const response = await companyService.registerCompany(data)
    if (response.isOk) {
      dispatch(getMyCompany()).then(() => {
        navigate('/moja-firma')
      })
      toast.success('Profile firme uspješno kreiran')
    } else {
      setRegistrationResponseErrors(response.message)
    }

    setRegistrationLoader(false)
  }

  const checkJib = async () => {
    if (!hasValue(formData.jib)) {
      setCompanyAlreadyExists(false)
      setCompanyAlreadyExistsData(null)
      return
    }

    const response = await companyService.checkIfCompanyExists({
      jib: formData.jib,
    })
    if (response.isOk) {
      if (response.data.exists) {
        setCompanyAlreadyExists(true)
        setCompanyAlreadyExistsData(response.data.company)
      } else {
        setCompanyAlreadyExists(false)
        setCompanyAlreadyExistsData(null)
      }
    }
  }

  const sendCompanyTransferRequest = async () => {
    if (!companyAlreadyExistsData) return

    setCompanyTransferRequestLoader(true)

    const data = {
      company_id: companyAlreadyExistsData.id,
    }

    const response =
      await companyTransferService.sendCompanyTransferRequest(data)
    if (response.isOk) {
      toast.success('Zahtjev za povezivanje firme uspješno poslan')
      setCompanyAlreadyExists(false)
      setCompanyAlreadyExistsData(null)
    } else {
      toast.error(
        response.message || 'Desila se greška prilikom slanja zahtjeva',
      )
    }
    setCompanyTransferRequestLoader(false)
  }

  useEffect(() => {
    if (!token) {
      navigate('/prijava')
    }

    // navigate to company profile if it exists
    if (token && myCompany !== null) {
      navigate('/moja-firma')
    }
  }, [token, myCompany, navigate])

  useEffect(() => {
    if (formData.jib?.length === 13) {
      checkJib()
    } else {
      setCompanyAlreadyExists(false)
      setCompanyAlreadyExistsData(null)
    }
  }, [formData.jib])

  const typeTitle = type.length > 0 ? type.toString() : 'Vrsta firme'

  return (
    <div id="add-company">
      <KonektoNavbar />
      <Container className="my-5">
        <h3>Kreirajte profil vaše firme</h3>
        <p className="text-muted">
          Ukoliko profil vaše firme već postoji na platformi, unesite JIB kako
          biste pokrenuli proces povezivanja firme s vašim računom.
        </p>
        <Form onSubmit={handleSubmit}>
          <div className="form-group-wrapper shadow">
            <h5>Osnovne informacije</h5>
            <hr className="hr divider" />
            <Form.Group controlId="jib" className="mb-3">
              <Form.Label>JIB *</Form.Label>
              <Form.Control
                type="text"
                name="jib"
                placeholder="Jedinstveni identifikacioni broj *"
                value={formData.jib}
                onChange={handleChange}
              />

              {companyAlreadyExists && (
                <div className="request-company-connection alert alert-warning d-flex justify-content-between align-items-start mt-2">
                  <span>
                    Firma sa ovim JIB-om već postoji.
                    <br />
                    Pošaljite zahtjev administratoru da poveže vašu firmu sa
                    vašim profilom.
                    <br />
                    <a
                      href={`/firma/${companyAlreadyExistsData?.id}`}
                      target="_blank"
                      className=""
                      rel="noopener noreferrer">
                      Pogledajte profil firme
                    </a>
                  </span>
                  <Button
                    className="rounded-pill primary-button"
                    size="sm"
                    disabled={companyTransferRequestLoader}
                    onClick={sendCompanyTransferRequest}>
                    {companyTransferRequestLoader ? (
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                      />
                    ) : (
                      'Pošalji zahtjev'
                    )}
                  </Button>
                </div>
              )}

              {registrationFormErrors?.jib ? (
                <span className="field-error">
                  {registrationFormErrors?.jib}
                </span>
              ) : null}
            </Form.Group>

            <Form.Group controlId="name" className="mb-3">
              <Form.Label>Ime firme *</Form.Label>
              <Form.Control
                type="text"
                name="name"
                placeholder="Ime firme *"
                value={formData.name}
                onChange={handleChange}
                // required
              />
              {registrationFormErrors?.name ? (
                <span className="field-error">
                  {registrationFormErrors?.name}
                </span>
              ) : null}
            </Form.Group>

            <Form.Group controlId="description" className="mb-3">
              <Form.Label className="w-50">O firmi</Form.Label>
              <Form.Control
                as="textarea"
                name="description"
                placeholder="O firmi"
                value={formData.description}
                onChange={handleChange}
                style={{ height: '150px' }} // Set the specific height for the textarea
              />
            </Form.Group>

            <Form.Group controlId="phone" className="mb-3">
              <Form.Label>Telefon *</Form.Label>
              <Form.Control
                type="text"
                name="phone"
                placeholder="Telefon *"
                value={formData.phone}
                onChange={handleChange}
                // required
              />
              {registrationFormErrors?.phone ? (
                <span className="field-error">
                  {registrationFormErrors?.phone}
                </span>
              ) : null}
            </Form.Group>
          </div>

          <div className="form-group-wrapper shadow">
            <h5>Lokacija</h5>
            <hr className="hr divider" />
            <Form.Group controlId="address" className="mb-3">
              <Form.Label>Adresa</Form.Label>
              <Form.Control
                type="text"
                name="address"
                placeholder="Adresa"
                value={formData.address}
                onChange={handleChange}
              />
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group controlId="zip_code" className="mb-3">
                  <Form.Label>Poštanski broj</Form.Label>
                  <Form.Control
                    type="text"
                    name="zipCode"
                    placeholder="Poštanski broj"
                    value={formData.zipCode}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="type" className="mb-3">
                  <Form.Label>Grad</Form.Label>
                  <Form.Select
                    title="Grad"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}>
                    <option key="default" value="" disabled>
                      Izaberite grad
                    </option>

                    {cities.map(option => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </div>

          <div className="form-group-wrapper shadow">
            <h5>Dodatne informacije</h5>
            <hr className="hr divider" />
            <Form.Group controlId="image" className="mb-3">
              <Form.Label>Logo firme</Form.Label>
              <div className="custom-file-upload">
                <input
                  type="file"
                  name="image"
                  id="image"
                  accept="image/*"
                  className="d-none"
                  onChange={handleFileChange} // Create this function to handle file selection
                />
                <label htmlFor="image" className="btn btn-outline-secondary">
                  Izaberite sliku
                </label>
              </div>
              {previewImage && (
                <div className="mt-3">
                  <img src={previewImage} alt="Odabrana slika" />
                </div>
              )}
            </Form.Group>

            <Form.Group controlId="email" className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                name="email"
                placeholder="Email"
                value={formData.email}
                onChange={handleChange}
              />
            </Form.Group>
            <Form.Group controlId="website" className="mb-3">
              <Form.Label>Web stranica</Form.Label>
              <Form.Control
                type="text"
                name="website"
                placeholder="Web stranica"
                value={formData.website}
                onChange={handleChange}
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group controlId="type" className="mb-3">
                  <Form.Label>Broj uposlenika</Form.Label>
                  <Form.Select
                    title="Broj uposlenika"
                    name="numOfEmployees"
                    value={formData.numOfEmployees}
                    onChange={handleChange}>
                    {NUM_OF_EMPLOYEES_OPTIONS.map(option => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group controlId="type" className="mb-3">
                  <Form.Label>Vrsta firme</Form.Label>
                  <DropdownButton
                    title={<span className="dropdown-title">{typeTitle}</span>}
                    className="custom-dropdown-button"
                    variant="outline-secondary"
                    autoClose="outside">
                    {typeOptions.map((option, index) => (
                      <Dropdown.Item
                        as="button"
                        key={index}
                        onClick={e => {
                          e.preventDefault()
                          toggleType(option)
                        }}
                        className="d-flex justify-content-between align-items-center custom-dropdown-item">
                        {option}
                        <input
                          type="checkbox"
                          checked={type.includes(option)}
                          readOnly
                          className="ms-2"
                        />
                      </Dropdown.Item>
                    ))}
                  </DropdownButton>
                </Form.Group>
              </Col>
            </Row>
          </div>

          {registrationResponseErrors ? (
            <p className="field-error text-center mb-0 mt-2">
              {registrationResponseErrors}
            </p>
          ) : null}

          <div className="text-end">
            <Button
              disabled={registrationLoader || companyAlreadyExists}
              type="submit"
              className="mt-3 px-5 rounded-pill primary-button">
              {registrationLoader ? (
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
              ) : (
                'Kreiraj profil'
              )}
            </Button>
          </div>
        </Form>
      </Container>
      <Footer />
    </div>
  )
}
